// src/components/BrandLoader.tsx
import { useContext, type CSSProperties } from "react";
import { useTheme } from "@mui/material/styles";
import Backdrop from "@mui/material/Backdrop";
import FadeLoader from "react-spinners/FadeLoader";
import { BrandingContext } from "@/branding/BrandingProvider";

type BrandLoaderProps = {
  /** Show an overlay (Backdrop) when true. If omitted, renders inline */
  open?: boolean;
  /** Force inline spinner even if `open` is provided */
  inline?: boolean;
  /** Optional override for spinner color (otherwise uses branding/theme) */
  color?: string;
  /** Optional CSS override for the spinner */
  cssOverride?: CSSProperties;
  /** Backdrop zIndex when using overlay */
  zIndex?: number;
  /** Accessible label for overlay */
  label?: string;
};

export default function BrandLoader({
  open,
  inline,
  color,
  cssOverride,
  zIndex,
  label = "Loading…",
}: BrandLoaderProps) {
  const branding = useContext(BrandingContext);
  const theme = useTheme();

  const resolvedColor =
    color ||
    branding?.loaderColor ||
    branding?.primaryColor ||
    theme.palette.primary.main;

  const defaultInlineCss: CSSProperties = {
    display: "block",
    position: "absolute",
    top: "30%",
    left: "50%",
    transform: "translateX(-50%)",
    width: "auto",
    height: 50,
    borderColor: resolvedColor,
  };

  const spinner = (
    <FadeLoader
      color={resolvedColor}
      loading
      cssOverride={{ ...(inline ? {} : defaultInlineCss), ...cssOverride } as any}
    />
  );

  // If `open` is boolean and not forced inline, wrap with a Backdrop
  if (typeof open === "boolean" && !inline) {
    return (
      <Backdrop
        open={open}
        aria-label={label}
        sx={{ zIndex: zIndex ?? ((t) => t.zIndex.drawer + 1) }}
      >
        <FadeLoader color={resolvedColor} loading cssOverride={{ display: "block" } as any} />
      </Backdrop>
    );
  }

  return spinner;
}
